import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Alert, Badge, Button, Card, Form, Spinner, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import PageLayouts from "../../../../layouts/PageLayout";

const apiUrl = process.env.REACT_APP_API_URL;
const imageBaseUrl = process.env.REACT_APP_IMG_URL || "";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser") || ""}`,
    Accept: "application/json",
});

const formatValue = (value) => (value === null || value === undefined || value === "" ? "—" : value);

const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const amount = Number(value);
    return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : String(value);
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const getImageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${imageBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

export default function SellerShippedProductDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [shipment, setShipment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [receivedImage, setReceivedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [receiving, setReceiving] = useState(false);
    const [receiveError, setReceiveError] = useState("");

    // Camera states & refs
    const [isCameraActive, setIsCameraActive] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [cameraError, setCameraError] = useState("");

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!id) {
                setError("Shipment ID was not provided.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setShipment(null);
            setError("");
            try {
                const response = await axios.get(
                    `${apiUrl}/admin/seller-shipped-products/${encodeURIComponent(id)}`,
                    { headers: authHeaders() }
                );
                const result = response.data?.data?.shipment || response.data?.data;
                if (!result || !Array.isArray(result.products)) {
                    throw new Error("Shipment details were not returned by the server.");
                }
                setShipment(result);
            } catch (err) {
                setError(err.response?.data?.message || err.message || "Could not load shipment details.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [id]);

    // Clean up camera stream on unmount
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    const startCamera = async () => {
        setCameraError("");
        setIsCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" },
                audio: false,
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Camera access error:", err);
            setCameraError("Unable to access the camera. Please check permissions.");
            setIsCameraActive(false);
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());
            videoRef.current.srcObject = null;
        }
        setIsCameraActive(false);
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const width = video.videoWidth || 640;
        const height = video.videoHeight || 480;

        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, width, height);

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], `receive_${Date.now()}.jpg`, { type: "image/jpeg" });
                setReceivedImage(file);
                setImagePreview(URL.createObjectURL(file));
                stopCamera();
            }
        }, "image/jpeg", 0.85);
    };

    const retakePhoto = () => {
        setReceivedImage(null);
        setImagePreview("");
        startCamera();
    };

    const receiveShipment = async () => {
        if (!shipment || receiving) return;
        const shipmentId = shipment.id;

        if (!shipmentId) {
            setReceiveError("Shipment ID not found. Please refresh and try again.");
            return;
        }

        if (!receivedImage) {
            setReceiveError("Please take a photo of the received shipment before confirming.");
            return;
        }

        setReceiving(true);
        setReceiveError("");

        try {
            const formData = new FormData();
            formData.append("received_image", receivedImage);

            const response = await axios.post(
                `${apiUrl}/admin/seller-shipments/${encodeURIComponent(shipmentId)}/receive`,
                formData,
                {
                    headers: {
                        ...authHeaders(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data?.success === false) {
                throw new Error(
                    response.data?.message || "The shipment receipt was not confirmed."
                );
            }

            await Swal.fire({
                icon: "success",
                title: "Shipment received",
                text: response.data?.message || "The shipment receipt was confirmed.",
                timer: 1800,
                showConfirmButton: false,
            });

            navigate(-1);
        } catch (requestError) {
            setReceiveError(
                requestError.response?.data?.message ||
                requestError.message ||
                "Could not receive this shipment."
            );
        } finally {
            setReceiving(false);
        }
    };

    if (loading) {
        return (
            <PageLayouts>
                <div className="text-center py-5"><Spinner animation="border" /></div>
            </PageLayouts>
        );
    }

    if (!shipment) {
        return (
            <PageLayouts>
                <div className="text-center py-5">
                    <Alert variant="danger">{error || "Shipment not found."}</Alert>
                    <Button variant="primary" onClick={() => navigate(-1)}>Back</Button>
                </div>
            </PageLayouts>
        );
    }

    const products = Array.isArray(shipment.products) ? shipment.products : [];
    const isReceived = shipment.status_2 !== null && shipment.status_2 !== undefined && shipment.status_2 !== "";

    return (
        <PageLayouts>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="mc-breadcrumb-title">Product Shipment Details</h3>
                        <p className="text-muted mb-0">Detailed view and confirmation of warehouse receipt.</p>
                    </div>
                    <Button variant="secondary" onClick={() => navigate(-1)}>Back to list</Button>
                </div>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            {receiveError && <Alert variant="danger" dismissible onClose={() => setReceiveError("")}>{receiveError}</Alert>}
            {cameraError && <Alert variant="danger" dismissible onClose={() => setCameraError("")}>{cameraError}</Alert>}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Shipment #{shipment.id}</h5>
                    {isReceived
                        ? <Badge bg="success">Received</Badge>
                        : <Badge bg="warning" text="dark">Pending Receive</Badge>}
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}><strong>Tracking number:</strong> {formatValue(shipment.tracking_number)}</Col>
                        <Col md={4}><strong>Courier:</strong> {formatValue(shipment.courier_company?.company_name)}</Col>
                        <Col md={4}><strong>Store:</strong> {formatValue(shipment.store?.name)}</Col>
                        <Col md={4}><strong>Seller:</strong> {formatValue(shipment.seller?.name)}</Col>
                        <Col md={4}><strong>Products:</strong> {formatValue(shipment.product_count ?? products.length)}</Col>
                        <Col md={4}><strong>Status:</strong> {formatValue(shipment.status_1)}{shipment.status_2 ? ` / ${shipment.status_2}` : ""}</Col>
                        <Col md={6}><strong>Dispatched:</strong> {formatDate(shipment.dispatched_at)}</Col>
                        <Col md={6}><strong>Received:</strong> {formatDate(shipment.received_at)}</Col>
                    </Row>
                    <Row className="g-3 mt-1">
                        {shipment.dispatched_image && (
                            <Col md={6}>
                                <strong className="d-block mb-2">Dispatch proof</strong>
                                <img
                                    src={getImageUrl(shipment.dispatched_image)}
                                    alt="Shipment dispatch proof"
                                    className="img-fluid rounded border"
                                    style={{ maxHeight: 260, objectFit: "contain" }}
                                />
                            </Col>
                        )}
                        {shipment.received_image && (
                            <Col md={6}>
                                <strong className="d-block mb-2">Receipt proof</strong>
                                <img
                                    src={getImageUrl(shipment.received_image)}
                                    alt="Shipment receipt proof"
                                    className="img-fluid rounded border"
                                    style={{ maxHeight: 260, objectFit: "contain" }}
                                />
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {!isReceived && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Header className="bg-white"><h5 className="mb-0">Confirm shipment receipt</h5></Card.Header>
                    <Card.Body className="text-center">
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                        {!isCameraActive && !imagePreview && (
                            <div className="py-4">
                                <p className="text-muted mb-3">Take a live photo to confirm receipt of the complete shipment.</p>
                                <Button variant="primary" size="lg" onClick={startCamera}>
                                    <i className="bi bi-camera me-2"></i> Open Camera
                                </Button>
                            </div>
                        )}
                        {isCameraActive && (
                            <div className="mb-3">
                                <div className="position-relative d-inline-block w-150">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        className="w-100 rounded border bg-dark"
                                        style={{ maxHeight: 320, objectFit: "cover" }}
                                    />
                                </div>
                                <div className="mt-3">
                                    <Button variant="success" size="lg" className="me-2" onClick={capturePhoto}>Capture Photo</Button>
                                    <Button variant="secondary" size="lg" onClick={stopCamera}>Cancel</Button>
                                </div>
                            </div>
                        )}
                        {imagePreview && (
                            <div className="mb-3">
                                <p className="text-success fw-bold mb-2">Photo captured successfully!</p>
                                <img
                                    src={imagePreview}
                                    alt="Captured receipt preview"
                                    className="rounded border mb-3"
                                    style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain" }}
                                />
                                <div>
                                    <Button variant="secondary" className="me-2" onClick={retakePhoto}>Retake Photo</Button>
                                    <Button variant="success" onClick={receiveShipment} disabled={receiving} size="lg">
                                        {receiving
                                            ? <><Spinner size="sm" animation="border" className="me-2" />Confirming...</>
                                            : "Confirm receipt"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Products in this shipment</h4>
                <Badge bg="secondary">{products.length} product(s)</Badge>
            </div>
            {products.length === 0 ? (
                <Alert variant="warning">No products are attached to this shipment.</Alert>
            ) : products.map((product) => {
                const images = Array.isArray(product.images) && product.images.length
                    ? product.images
                    : product.card_img ? [{ id: "card", image: product.card_img }] : [];
                const attributes = Array.isArray(product.attributes)
                    ? product.attributes.map((attribute) => [attribute?.name, attribute?.value])
                    : Object.entries(product.attributes || {}).map(([name, value]) => [
                        value && typeof value === "object" ? value.name || name : name,
                        value && typeof value === "object" ? value.value : value,
                    ]);

                return (
                    <Card className="border-0 shadow-sm mb-4" key={product.shipment_item_id || product.id}>
                        <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="mb-1">{formatValue(product.title)}</h5>
                                    <Badge bg="primary">{formatValue(product.product_code)}</Badge>
                                </div>
                                <small className="text-muted">Product ID: {formatValue(product.id)}</small>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {images.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mb-4">
                                    {images.map((image) => (
                                        <img
                                            key={image.id || image.image}
                                            src={getImageUrl(image.image)}
                                            alt={product.title || "Product"}
                                            className="rounded border"
                                            style={{ width: 120, height: 120, objectFit: "contain" }}
                                        />
                                    ))}
                                </div>
                            )}
                            <Row className="g-3">
                                <Col md={4}><strong>Brand:</strong> {formatValue(product.brand?.name)}</Col>
                                <Col md={4}><strong>Category:</strong> {formatValue(product.category?.name)}</Col>
                                <Col md={4}><strong>Condition:</strong> {formatValue(product.product_condition)}</Col>
                                <Col md={4}><strong>Selling style:</strong> {formatValue(product.selling_style)}</Col>
                                <Col md={4}><strong>Quantity:</strong> {formatValue(product.qty)}</Col>
                                <Col md={4}><strong>Buying price:</strong> {formatMoney(product.buying_price)}</Col>
                                <Col md={4}><strong>Sell price:</strong> {formatMoney(product.sell_price_dollar)}</Col>
                                <Col md={4}><strong>Shipping charges:</strong> {formatMoney(product.shipping_charges_dollar)}</Col>
                                {attributes.map(([name, value], index) => (
                                    <Col md={4} key={`${name}-${index}`}>
                                        <strong>{formatValue(name)}:</strong> {formatValue(value)}
                                    </Col>
                                ))}
                                {product.short_description && (
                                    <Col xs={12}>
                                        <strong>Short description</strong>
                                        <div>{product.short_description}</div>
                                    </Col>
                                )}
                                {product.description && (
                                    <Col xs={12}>
                                        <strong>Description</strong>
                                        <div>{product.description}</div>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                );
            })}
        </PageLayouts>
    );
}